import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Alert, Box, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  StyledWaitlistDialog,
  WaitlistDialogActions,
  WaitlistDialogCloseButton,
  WaitlistDialogContent,
  WaitlistDialogTitle,
  WaitlistField,
  WaitlistPanel,
  WaitlistPrimaryButton,
  WaitlistSecondaryButton,
} from './AdminWaitlist.styled';

function platformBusinessId(business) {
  return business?.BusinessId || business?.id || '';
}

function leadDisplayName(lead) {
  const requester = lead?.requester || {};
  return (
    [requester.firstName, requester.lastName].filter(Boolean).join(' ') ||
    'this homeowner'
  );
}

function AdminWaitlistAssignModal({
  open,
  lead,
  businesses = [],
  businessesLoading = false,
  businessesError = null,
  onClose,
  onAssign,
  saving = false,
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setSearch(String(lead?.city || '').trim());
    setSelectedId('');
    setError(null);
  }, [open, lead]);

  const sortedBusinesses = useMemo(
    () =>
      [...businesses].sort((a, b) =>
        String(a.BusinessName || '').localeCompare(
          String(b.BusinessName || ''),
          undefined,
          { sensitivity: 'base' },
        ),
      ),
    [businesses],
  );

  const filteredBusinesses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedBusinesses;
    return sortedBusinesses.filter((business) => {
      const haystack = [
        business.BusinessName,
        platformBusinessId(business),
        business.BusinessEmail,
        business.BusinessPhoneNumber,
        business.BusinessAddress?.City,
        business.BusinessAddress?.State,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedBusinesses, search]);

  const handleAssign = async () => {
    if (!selectedId) {
      setError('Select a business to assign this lead to.');
      return;
    }
    setError(null);
    try {
      await onAssign(selectedId);
    } catch (e) {
      setError(e.message || 'Failed to assign lead to business');
    }
  };

  return (
    <StyledWaitlistDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="admin-waitlist-assign-title"
    >
      <WaitlistDialogTitle id="admin-waitlist-assign-title">
        <span className="waitlist-dialog-icon-wrap" aria-hidden>
          <StorefrontOutlinedIcon sx={{ color: '#0d4efe' }} />
        </span>
        Assign to a business
        <WaitlistDialogCloseButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </WaitlistDialogCloseButton>
      </WaitlistDialogTitle>

      <WaitlistDialogContent>
        <Typography variant="body2" sx={{ color: '#5a6478' }}>
          Move{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            {leadDisplayName(lead)}
          </Box>
          ’s waitlist request into a provider’s inbox.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            {error}
          </Alert>
        ) : null}

        <WaitlistField
          autoFocus
          fullWidth
          size="small"
          label="Search businesses"
          placeholder="Name, email, city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={saving}
        />

        <WaitlistPanel sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {businessesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <NkySpinner />
              </Box>
            ) : businessesError ? (
              <Typography sx={{ color: '#5a6478', p: 2 }}>
                {businessesError}
              </Typography>
            ) : filteredBusinesses.length === 0 ? (
              <Typography sx={{ color: '#5a6478', p: 2 }}>
                No businesses match “{search.trim() || 'your search'}”.
              </Typography>
            ) : (
              <List disablePadding dense>
                {filteredBusinesses.map((business) => {
                  const id = platformBusinessId(business);
                  const selected = selectedId === id;
                  const city = business.BusinessAddress?.City;
                  return (
                    <ListItemButton
                      key={id}
                      selected={selected}
                      disabled={saving}
                      onClick={() => setSelectedId(id)}
                      sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(13, 78, 254, 0.08)',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(13, 78, 254, 0.12)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={business.BusinessName || id}
                        secondary={
                          [city, business.BusinessEmail].filter(Boolean).join(' · ') ||
                          null
                        }
                        primaryTypographyProps={{ fontWeight: selected ? 700 : 600 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </WaitlistPanel>
      </WaitlistDialogContent>

      <WaitlistDialogActions>
        <WaitlistSecondaryButton
          variant="outlined"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </WaitlistSecondaryButton>
        <WaitlistPrimaryButton
          onClick={handleAssign}
          disabled={saving || !selectedId}
        >
          {saving ? 'Assigning…' : 'Assign'}
        </WaitlistPrimaryButton>
      </WaitlistDialogActions>
    </StyledWaitlistDialog>
  );
}

export default AdminWaitlistAssignModal;
